/* Strategy editor: free-text custom interventions (classified server-side
   by the suggester, LLM in M2), plain-language impact profiles, live
   metrics via the local engine, template starting points, and
   description -> portfolio proposals. */
import React, { useMemo, useState } from 'react';
import {
  IV_PRESETS, IV_SIZE, IV_OPEX, IV_SPEED, LEVER_KEYS, LEVER_META, PRIORITIES,
  STRAT_TEMPLATES, instantiatePreset,
  type InterventionProfile, type IvOpex, type IvSize, type IvSpeed, type Priority, type Strategy,
} from '@flourish/schema';
import type { PortfolioProposal } from '@flourish/suggester';
import { I, MiniBars, Seg } from '../ui.js';
import { useStore, activeEntity, type DrawerState } from '../store.jsx';
import { api } from '../api.js';
import { Drawer } from './Drawers.jsx';
import { interventionStats, pareto, toWeights } from '../local.js';

const uid = () => 'iv_' + Math.random().toString(36).slice(2, 10);
const nearestLevel = (v: number) => [0, 0.3, 0.6, 0.9].reduce((b, l) => Math.abs(v - l) < Math.abs(v - b) ? l : b, 0);

function ProfilePanel({ iv, onChange }: { iv: InterventionProfile; onChange: (patch: Partial<InterventionProfile>) => void }) {
  const preset = iv.ref ? IV_PRESETS[iv.ref] : null;
  return (
    <div className="iv-profile">
      {LEVER_KEYS.map(k => (
        <div className="lv-row" key={k}>
          <div className="lv-lbl"><span className="lv-name">{LEVER_META[k].label}</span><span className="lv-q">{LEVER_META[k].q}</span></div>
          <Seg
            options={[{ label: 'None', v: '0' }, { label: 'Slight', v: '0.3' }, { label: 'Strong', v: '0.6' }, { label: 'Core', v: '0.9' }]}
            value={String(nearestLevel(iv.levers[k]))}
            onChange={v => onChange({ levers: { ...iv.levers, [k]: parseFloat(v) } })}
          />
        </div>
      ))}
      <div className="lv-row">
        <div className="lv-lbl"><span className="lv-name">Investment size</span><span className="lv-q">One-off effort to put this in place</span></div>
        <Seg options={Object.entries(IV_SIZE).map(([v, m]) => ({ label: m.label, v: v as IvSize }))} value={iv.size} onChange={size => onChange({ size })} />
      </div>
      <div className="lv-row">
        <div className="lv-lbl"><span className="lv-name">Running cost</span><span className="lv-q">Ongoing effect on operating cost</span></div>
        <Seg options={Object.entries(IV_OPEX).map(([v, m]) => ({ label: m.label, v: v as IvOpex }))} value={iv.opex} onChange={opex => onChange({ opex })} />
      </div>
      <div className="lv-row">
        <div className="lv-lbl"><span className="lv-name">Time to full effect</span><span className="lv-q">How long until it delivers</span></div>
        <Seg options={Object.entries(IV_SPEED).map(([v, m]) => ({ label: m.label, v: v as IvSpeed }))} value={iv.speed} onChange={speed => onChange({ speed })} />
      </div>
      <div className="iv-profile-foot">
        <span className="muted" style={{ fontSize: 10.5 }}>Size, cost &amp; timing feed the dynamic model directly — every dial changes the numbers.</span>
        {preset && (
          <button className="btn sm" title="Restore the calibrated library profile"
            onClick={() => onChange({ levers: { efficiency: 0, resilience: 0, growth: 0, diversification: 0, ...preset.levers }, size: preset.size, opex: preset.opex, speed: preset.speed, tags: [...preset.tags] })}>
            Reset to library profile
          </button>
        )}
      </div>
    </div>
  );
}

export function StrategyEditor({ d }: { d: Extract<DrawerState, { kind: 'strategy-editor' }> }) {
  const { s, set, toast, refreshStrategies } = useStore();
  const e = activeEntity(s)!;
  const existing = d.strategyId ? (s.strategiesByEntity[e.id] ?? []).find(x => x.id === d.strategyId) : null;
  const isNew = !existing;

  const [name, setName] = useState(existing?.name ?? 'New Strategy');
  const [desc, setDesc] = useState(existing?.desc ?? '');
  const [ivs, setIvs] = useState<InterventionProfile[]>(existing ? existing.interventions.map(iv => ({ ...iv, levers: { ...iv.levers }, tags: [...iv.tags] })) : []);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [customText, setCustomText] = useState('');
  const [proposals, setProposals] = useState<PortfolioProposal[] | null>(null);
  const [busy, setBusy] = useState(false);

  const draft: Strategy = useMemo(() => ({
    id: existing?.id ?? '_draft', name, desc, isNoRegret: !!existing?.isNoRegret, isMostLikely: false, interventions: ivs,
  }), [existing, name, desc, ivs]);

  const weights = toWeights(s.scenarios);
  const stats = useMemo(() => interventionStats(e, draft, weights), [e, draft, weights]);
  const pz = useMemo(() => pareto(e, draft, weights), [e, draft, weights]);

  const patchIv = (iid: string, patch: Partial<InterventionProfile>) =>
    setIvs(list => list.map(iv => iv.iid === iid ? { ...iv, ...patch } : iv));

  const addPreset = (ref: string) =>
    setIvs(list => [...list, instantiatePreset(ref, { iid: uid(), priority: 'Medium' })]);

  const addCustom = async () => {
    const text = customText.trim();
    if (text.length < 8) { toast('Describe the intervention in a few words first'); return; }
    setBusy(true);
    try {
      const res = await api.classify(text);
      const iv = { ...res.profile, iid: uid() };
      setIvs(list => [...list, iv]);
      setOpen(o => ({ ...o, [iv.iid]: true }));
      setCustomText('');
      toast(res.suggestion.confidence === 'none'
        ? 'Added — no clear signals recognized, set the impact profile manually'
        : 'Added with a suggested profile — review & adjust');
    } catch (err) { toast((err as Error).message); }
    setBusy(false);
  };

  const acceptProposal = (p: PortfolioProposal) => {
    if (p.kind === 'preset') {
      if (!ivs.some(iv => iv.ref === p.ref)) addPreset(p.ref);
    } else {
      setIvs(list => [...list, {
        iid: uid(), ref: null, custom: true, name: p.name, desc: p.clause, owner: p.profile.owner,
        priority: 'Medium', levers: p.profile.levers, size: p.profile.size, opex: p.profile.opex,
        speed: p.profile.speed, tags: p.profile.tags,
      }]);
    }
    setProposals(list => (list ?? []).filter(x => x !== p));
  };

  const suggestFromDesc = async () => {
    if (desc.trim().length < 20) { toast('Write a few sentences of description first'); return; }
    setBusy(true);
    try { setProposals((await api.suggestPortfolio(desc)).proposals); }
    catch (err) { toast((err as Error).message); }
    setBusy(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      if (existing) await api.updateStrategy(existing.id, { name, desc, interventions: ivs });
      else await api.createStrategy(e.id, { name: name || 'Untitled Strategy', desc, interventions: ivs });
      await refreshStrategies(e.id);
      set({ drawer: null });
      toast(existing ? 'Strategy updated' : 'Strategy added');
    } catch (err) { toast((err as Error).message); }
    setBusy(false);
  };
  const remove = async () => {
    if (!existing) return;
    try { await api.deleteStrategy(existing.id); await refreshStrategies(e.id); set({ drawer: null }); toast('Strategy removed'); }
    catch (err) { toast((err as Error).message); }
  };

  const usedRefs = new Set(ivs.map(iv => iv.ref).filter(Boolean));
  const usedNames = new Set(ivs.map(iv => iv.name.toLowerCase()));
  const lib = Object.keys(IV_PRESETS).filter(ref => !usedRefs.has(ref));
  const mine = (s.customIvsByEntity[e.id] ?? []).filter(c => !usedNames.has(c.name.toLowerCase()));

  return (
    <Drawer open onClose={() => set({ drawer: null })}
      eyebrow={isNew ? 'New strategy' : 'Edit strategy'}
      title={isNew ? 'Design a strategy' : existing!.name}
      foot={<>
        {!isNew && !existing!.isNoRegret && <button className="btn" onClick={() => void remove()}><I k="trash" /> Delete</button>}
        <div className="grow" />
        <button className="btn" onClick={() => set({ drawer: null })}>Cancel</button>
        <button className="btn primary" disabled={busy} onClick={() => void save()}><I k="check" /> {isNew ? 'Add strategy' : 'Save'}</button>
      </>}>
      <div className="field"><label>Name</label>
        <input className="input" value={name} placeholder="e.g. Regional resilience" onChange={ev => setName(ev.target.value)} /></div>
      <div className="field"><label>Description <span className="muted" style={{ textTransform: 'none', fontWeight: 400 }}>— free text, in your own words</span></label>
        <textarea className="textarea" value={desc} placeholder="Describe the posture and thesis of this strategy…" onChange={ev => setDesc(ev.target.value)} />
        <button className="btn sm suggest-btn" disabled={busy} onClick={() => void suggestFromDesc()} title="Read the description and propose interventions">
          <I k="wand" /> Suggest interventions from description
        </button>
      </div>

      {proposals && (
        proposals.length === 0
          ? <div className="callout warn" style={{ marginBottom: 14 }}><div className="ico"><I k="info" /></div><div>No clear interventions recognized — try naming concrete moves (e.g. “build cash reserves”, “recycle materials”, “expand into new regions”).</div></div>
          : <div className="sug-panel">
              <div className="sug-head"><I k="wand" /> Suggested from your description <span className="muted">— accept what fits</span>
                <span className="grow" />
                <button className="btn sm" onClick={() => { proposals.forEach(acceptProposal); }}>Add all</button>
                <button className="mini" title="Dismiss" onClick={() => setProposals(null)}><I k="close" /></button>
              </div>
              {proposals.map((p, i) => {
                const levers = p.kind === 'preset'
                  ? { efficiency: 0, resilience: 0, growth: 0, diversification: 0, ...IV_PRESETS[p.ref].levers }
                  : p.profile.levers;
                const nm = p.kind === 'preset' ? IV_PRESETS[p.ref].name : p.name;
                return (
                  <div className="sug-row" key={i}>
                    <MiniBars levers={levers} />
                    <div className="sug-main">
                      <div className="sug-name">{nm} <span className={`iv-badge ${p.kind === 'preset' ? 'lib' : 'custom'}`}>{p.kind === 'preset' ? 'library' : 'custom'}</span></div>
                      <div className="sug-clause">“{p.clause.length > 90 ? p.clause.slice(0, 90) + '…' : p.clause}”</div>
                    </div>
                    <button className="btn sm primary" onClick={() => acceptProposal(p)}><I k="plus" /> Add</button>
                  </div>
                );
              })}
            </div>
      )}

      {isNew && (
        <div className="field"><label>Starting points <span className="muted" style={{ textTransform: 'none', fontWeight: 400 }}>— optional, everything stays editable</span></label>
          <div className="chips">
            {STRAT_TEMPLATES.map(t => (
              <button key={t.key} className="chip" onClick={() => {
                setName(t.name); setDesc(t.desc);
                setIvs(t.ivs.map((ref, i) => instantiatePreset(ref, { iid: uid(), priority: i === 0 ? 'High' : 'Medium' })));
              }}>{t.name}</button>
            ))}
          </div>
        </div>
      )}

      <div className="section-h">Interventions <span style={{ textTransform: 'none', color: 'var(--ink-3)', fontWeight: 400 }}>— the concrete moves this strategy is made of</span></div>

      {ivs.length === 0 && <div className="muted" style={{ fontSize: 12, padding: '6px 2px' }}>No interventions yet — describe your own below, pick from the library, or start from a template.</div>}
      {ivs.map(iv => (
        <div className={`iv-edit ${open[iv.iid] ? 'open' : ''}`} key={iv.iid}>
          <div className="iv-edit-top">
            <input className="input sm" value={iv.name} placeholder="Intervention name" onChange={ev => patchIv(iv.iid, { name: ev.target.value })} />
            <button className="mini danger" title="Remove" onClick={() => setIvs(list => list.filter(x => x.iid !== iv.iid))}><I k="trash" /></button>
          </div>
          <div className="iv-edit-meta">
            <span className="owner-wrap"><I k="owner" /><input className="owner-input" value={iv.owner} placeholder="Owner" onChange={ev => patchIv(iv.iid, { owner: ev.target.value })} /></span>
            <select className="select sm" value={iv.priority} onChange={ev => patchIv(iv.iid, { priority: ev.target.value as Priority })}>
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
            <button className={`impact-btn ${open[iv.iid] ? 'on' : ''}`} title="Impact profile — what this intervention does in the model"
              onClick={() => setOpen(o => ({ ...o, [iv.iid]: !o[iv.iid] }))}>
              <MiniBars levers={iv.levers} /><span>Impact</span><I k={open[iv.iid] ? 'chevD' : 'chevR'} />
            </button>
          </div>
          {(iv.custom) && <div className="iv-badges"><span className="iv-badge custom">custom</span></div>}
          {open[iv.iid] && <ProfilePanel iv={iv} onChange={patch => patchIv(iv.iid, patch)} />}
        </div>
      ))}

      <div className="iv-add">
        <div className="iv-add-lbl">Add an intervention</div>
        <div className="iv-custom-add">
          <input className="input sm" value={customText}
            placeholder="Describe an intervention in your own words — e.g. “nearshore our suppliers to regional partners”"
            onChange={ev => setCustomText(ev.target.value)}
            onKeyDown={ev => { if (ev.key === 'Enter') void addCustom(); }} />
          <button className="btn sm primary" disabled={busy} onClick={() => void addCustom()}><I k="wand" /> Add</button>
        </div>
        {mine.length > 0 && (
          <>
            <div className="iv-add-lbl sub">from my interventions</div>
            <div className="chips">
              {mine.map(c => (
                <button key={c.name} className="chip mine" onClick={() => setIvs(list => [...list, { ...c, iid: uid() }])}>
                  <I k="plus" /> {c.name}
                  <span className="chip-x" title="Remove from my interventions"
                    onClick={async ev => { ev.stopPropagation(); await api.deleteCustomIv(e.id, c.name); await refreshStrategies(e.id); }}>
                    <I k="close" />
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
        <div className="iv-add-lbl sub">or start from the library</div>
        <div className="chips">
          {lib.length ? lib.map(ref => (
            <button key={ref} className="chip" onClick={() => addPreset(ref)}><I k="plus" /> {IV_PRESETS[ref].name}</button>
          )) : <span className="muted" style={{ fontSize: 11.5 }}>All library interventions added</span>}
        </div>
      </div>

      <div className="mini-metrics">
        <div className="mm"><span className="v mono">{stats.count}</span><span className="l"><I k="layers" />Interventions</span></div>
        <div className="mm pos"><span className="v mono">{stats.pos}</span><span className="l"><I k="arrowUp" />Positive</span></div>
        <div className="mm neg"><span className="v mono">{stats.neg}</span><span className="l"><I k="arrowDown" />Negative</span></div>
      </div>
      {stats.count > 0 && (
        <div className="callout info" style={{ marginTop: 10 }}>
          <div className="ico"><I k="target" /></div>
          <div><strong>80/20 —</strong> {pz.top.length} of {stats.count} intervention{pz.top.length === 1 ? ' carries' : 's carry'} ~{pz.share}% of the impact
            {pz.top.length > 0 && <>: <strong>{pz.top.map(t => t.name).join(', ')}</strong></>}.</div>
        </div>
      )}
    </Drawer>
  );
}
